import { MappedAdvancedSearchResult, resultMapper } from 'search/resultUtils';

import {
  AdvancedSearchStateShape,
  PayloadAdvancedSearchError,
  PayloadQueryAdvancedSearch,
  PayloadQueryAdvancedSearchSuccess,
  PayloadSelectCondition,
  PayloadSelectFields,
  PayloadSelectFilterType,
  PayloadSelectPage,
  PayloadSelectSnapshot,
  PayloadUpdateAdvancedSearch,
} from './types';
import { defaultState } from './consts';
import {
  extractConditionFromCombinedRules,
  extractFilterTypeFromCombinedRules,
  extractQueryBuilderRulesFromCombinedRules,
} from './utils';
import { logError } from '@ardoq/logging';
import {
  UnknownDocumentType,
  SearchBackend,
  QueryBuilderQuery,
  AdvancedSearchField,
} from '@ardoq/api-types';
import { PayloadStoredQuery } from 'search/actions';

const updateAdvancedSearchReducer = (
  currentState: AdvancedSearchStateShape,
  { queryBuilderRules }: PayloadUpdateAdvancedSearch
): AdvancedSearchStateShape => ({
  ...currentState,
  searchError: null,
  queryBuilderRules,
});

const selectConditionReducer = (
  currentState: AdvancedSearchStateShape,
  { condition }: PayloadSelectCondition
): AdvancedSearchStateShape => ({
  ...currentState,
  selectedCondition: condition,
  queryBuilderRules: currentState.queryBuilderRules
    ? { ...currentState.queryBuilderRules, condition }
    : null,
});

const selectFilterTypeReducer = (
  currentState: AdvancedSearchStateShape,
  { filterType }: PayloadSelectFilterType
): AdvancedSearchStateShape => ({
  ...currentState,
  queryBuilderRules: null,
  selectedFilterType: filterType,
  selectedCondition: defaultState.selectedCondition,
});

const queryAdvancedSearchSuccessReducer = (
  currentState: AdvancedSearchStateShape,
  { total, results, fields }: PayloadQueryAdvancedSearchSuccess
): AdvancedSearchStateShape => ({
  ...currentState,
  total,
  resultFields: fields.map(
    ({ name, label, type, numberFormatOptions }): AdvancedSearchField => ({
      name,
      label,
      type,
      numberFormatOptions,
    })
  ),
  results: results.map(
    resultMapper<PayloadQueryAdvancedSearchSuccess['results'][0]['doc']>
  ),
  showResults: true,
});

const advancedSearchErrorReducer = (
  currentState: AdvancedSearchStateShape,
  { searchError }: PayloadAdvancedSearchError
): AdvancedSearchStateShape => ({
  ...currentState,
  results: [],
  searchError,
  isExporting: false,
  showResults: false,
});

const queryAdvancedSearchReducer = (
  currentState: AdvancedSearchStateShape,
  { sortOrder, sortBy, from, searchBackend }: PayloadQueryAdvancedSearch
): AdvancedSearchStateShape => ({
  ...currentState,
  searchError: null,
  sortBy,
  sortOrder,
  searchBackend,
  selectedPage: from === undefined ? 1 : currentState.selectedPage,
});

const resetAllSearchStateReducer = (): AdvancedSearchStateShape => ({
  ...defaultState,
});

const selectPageReducer = (
  currentState: AdvancedSearchStateShape,
  { pageNumber }: PayloadSelectPage
): AdvancedSearchStateShape => ({
  ...currentState,
  selectedPage: pageNumber,
});

const selectSnapshotReducer = (
  currentState: AdvancedSearchStateShape,
  { snapshot }: PayloadSelectSnapshot
): AdvancedSearchStateShape => ({
  ...currentState,
  selectedSnapshot: snapshot,
});

const selectFieldsReducer = (
  currentState: AdvancedSearchStateShape,
  { selectedFieldNames }: PayloadSelectFields
): AdvancedSearchStateShape => ({
  ...currentState,
  selectedFieldNames,
});

const exportAdvancedSearchReducer = (
  currentState: AdvancedSearchStateShape
): AdvancedSearchStateShape => ({
  ...currentState,
  isExporting: true,
  results: [] as MappedAdvancedSearchResult<UnknownDocumentType>[],
});

const exportAdvancedSearchSuccessReducer = (
  currentState: AdvancedSearchStateShape
): AdvancedSearchStateShape => ({
  ...currentState,
  isExporting: false,
});

const loadStoredQueryReducer = (
  currentState: AdvancedSearchStateShape,
  { storedQuery }: PayloadStoredQuery
) => {
  if (storedQuery.backend === SearchBackend.ADVANCED_SEARCH) {
    const combinedRules = storedQuery.query as QueryBuilderQuery;
    const queryBuilderRules = combinedRules
      ? extractQueryBuilderRulesFromCombinedRules(combinedRules)
      : defaultState.queryBuilderRules;
    const selectedFilterType = combinedRules
      ? extractFilterTypeFromCombinedRules(combinedRules)
      : defaultState.selectedFilterType;
    const isMissingRules = combinedRules.rules === undefined;
    if (isMissingRules) {
      logError(Error('Unrecognized stored query.'), null, {
        info:
          typeof combinedRules === 'string'
            ? combinedRules
            : Object.keys(combinedRules),
      });
    }
    const selectedCondition = isMissingRules
      ? defaultState.selectedCondition
      : (extractConditionFromCombinedRules(combinedRules) ??
        defaultState.selectedCondition);

    return {
      ...defaultState,
      selectedQueryId: storedQuery._id,
      queryBuilderRules,
      selectedFilterType,
      selectedCondition,
    };
  }
  return currentState;
};

export const advancedSearchOperations = {
  updateAdvancedSearchReducer,
  selectConditionReducer,
  selectFilterTypeReducer,
  queryAdvancedSearchSuccessReducer,
  advancedSearchErrorReducer,
  queryAdvancedSearchReducer,
  resetAllSearchStateReducer,
  selectPageReducer,
  selectSnapshotReducer,
  selectFieldsReducer,
  exportAdvancedSearchReducer,
  exportAdvancedSearchSuccessReducer,
  loadStoredQueryReducer,
};
